import React, { useState } from 'react';
import axios from 'axios';
import { useReveal } from '../lib/useReveal';
import { toast } from 'sonner';
import { Check, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUDGETS = [
  '$1,000 – $2,500',
  '$2,500 – $5,000',
  '$5,000 – $10,000',
  '$10,000+',
];

const STYLES = [
  'Private Table Journey',
  'Cellar & Table Experience',
  'Estate Escape',
  'Chef’s Provenance',
  'Bespoke Concierge',
  'Undecided — please advise',
];

const PRIVACY = ['Intimate / discreet', 'Private dining room', 'Fully private venue', 'No preference'];
const TRANSPORT = ['Not required', 'Chauffeured sedan', 'Chauffeured SUV', 'Helicopter / premium transfer'];

const empty = {
  name: '', email: '', phone: '',
  preferred_date: '', occasion: '', guests: '',
  location: '', experience_style: '',
  food_preferences: '', beverage_preferences: '', dietary: '',
  privacy_level: '', transport: '', budget_range: '',
  notes: '',
};

export default function Enquire() {
  const ref = useReveal();
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Please share your name and email so we can respond personally.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
      toast.success('Your enquiry has been received. We will respond personally within one business day.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'We could not submit your enquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div ref={ref} data-testid="enquire-page" className="pt-36 sm:pt-44 lg:pt-56 bg-[color:var(--tdt-ivory)]">
      <section className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-12 pb-12 lg:pb-20">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7 reveal">
            <div className="tdt-eyebrow mb-5">Enquire Privately</div>
            <h1 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-[color:var(--tdt-ink)] mb-8">
              Begin a private conversation.
            </h1>
            <p className="text-base sm:text-lg lg:text-lg leading-relaxed text-[color:var(--tdt-muted)] max-w-xl">
              Share a few details about your occasion. We respond personally to every enquiry, usually within one business day, with a tailored proposal and recommendations.
            </p>
          </div>
          <div className="lg:col-span-5 reveal media-card relative aspect-[4/3]">
            <img src="/assets/photos/champagne-rooftop.jpg" alt="Champagne flutes on a rooftop" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="bg-[color:var(--tdt-cream)] py-14 sm:py-18 lg:py-28">
        <div className="max-w-[1000px] mx-auto px-5 sm:px-6 lg:px-12">
          {done ? (
            <div data-testid="enquiry-success" className="text-center py-12 reveal in">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[color:var(--tdt-gold)]/15 text-[color:var(--tdt-gold)] mb-8">
                <Check size={28} />
              </div>
              <div className="tdt-eyebrow mb-5">Enquiry Received</div>
              <h2 className="font-serif font-light text-3xl sm:text-4xl md:text-5xl leading-[1.05] text-[color:var(--tdt-ink)] mb-6">
                Thank you, {form.name.split(' ')[0] || 'guest'}.
              </h2>
              <p className="text-[color:var(--tdt-muted)] max-w-xl mx-auto leading-relaxed mb-10">
                Your enquiry has been received. A member of the Tour de Taste team will respond personally, usually within one business day, with considered next steps.
              </p>
              <button
                data-testid="new-enquiry-btn"
                onClick={() => { setForm(empty); setDone(false); }}
                className="btn-pill btn-outline-ink"
              >
                Submit another enquiry
              </button>
            </div>
          ) : (
            <form data-testid="enquiry-form" noValidate onSubmit={submit} className="space-y-14">
              {/* Section 1: Guest */}
              <div className="reveal">
                <h3 className="font-serif text-2xl text-[color:var(--tdt-ink)] mb-2">Your details</h3>
                <div className="hr-gold mb-10" />
                <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
                  <Field label="Name" required>
                    <input data-testid="field-name" value={form.name} onChange={update('name')} className="tdt-input" placeholder="Full name" />
                  </Field>
                  <Field label="Email" required>
                    <input data-testid="field-email" type="email" value={form.email} onChange={update('email')} className="tdt-input" placeholder="you@example.com" />
                  </Field>
                  <Field label="Phone">
                    <input data-testid="field-phone" value={form.phone} onChange={update('phone')} className="tdt-input" placeholder="Including country code" />
                  </Field>
                  <Field label="Preferred date">
                    <input data-testid="field-date" value={form.preferred_date} onChange={update('preferred_date')} className="tdt-input" placeholder="e.g. Saturday 18 May, or a weekend in June" />
                  </Field>
                </div>
              </div>

              {/* Section 2: Occasion */}
              <div className="reveal">
                <h3 className="font-serif text-2xl text-[color:var(--tdt-ink)] mb-2">The occasion</h3>
                <div className="hr-gold mb-10" />
                <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
                  <Field label="Occasion">
                    <input data-testid="field-occasion" value={form.occasion} onChange={update('occasion')} className="tdt-input" placeholder="Anniversary, proposal, client hosting…" />
                  </Field>
                  <Field label="Number of guests">
                    <input data-testid="field-guests" value={form.guests} onChange={update('guests')} className="tdt-input" placeholder="e.g. 2 guests, or party of 6" />
                  </Field>
                  <Field label="Preferred location or region">
                    <input data-testid="field-location" value={form.location} onChange={update('location')} className="tdt-input" placeholder="City, wine region, or open to recommendation" />
                  </Field>
                  <Field label="Style of experience">
                    <select data-testid="field-style" value={form.experience_style} onChange={update('experience_style')} className="tdt-select">
                      <option value="">Select a journey style</option>
                      {STYLES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                </div>
              </div>

              {/* Section 3: Preferences */}
              <div className="reveal">
                <h3 className="font-serif text-2xl text-[color:var(--tdt-ink)] mb-2">Preferences</h3>
                <div className="hr-gold mb-10" />
                <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
                  <Field label="Food preferences">
                    <textarea data-testid="field-food" value={form.food_preferences} onChange={update('food_preferences')} className="tdt-textarea" placeholder="Cuisines, styles or favourite venues" />
                  </Field>
                  <Field label="Wine or beverage preferences">
                    <textarea data-testid="field-bev" value={form.beverage_preferences} onChange={update('beverage_preferences')} className="tdt-textarea" placeholder="Varietals, regions, spirits, non-alcoholic" />
                  </Field>
                  <Field label="Dietary requirements">
                    <textarea data-testid="field-diet" value={form.dietary} onChange={update('dietary')} className="tdt-textarea" placeholder="Allergies, intolerances, preferences" />
                  </Field>
                  <Field label="Desired level of privacy">
                    <select data-testid="field-privacy" value={form.privacy_level} onChange={update('privacy_level')} className="tdt-select">
                      <option value="">Select privacy level</option>
                      {PRIVACY.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Transport requirements">
                    <select data-testid="field-transport" value={form.transport} onChange={update('transport')} className="tdt-select">
                      <option value="">Select transport</option>
                      {TRANSPORT.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Budget range">
                    <select data-testid="field-budget" value={form.budget_range} onChange={update('budget_range')} className="tdt-select">
                      <option value="">Select investment range</option>
                      {BUDGETS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                </div>
              </div>

              {/* Section 4: Notes */}
              <div className="reveal">
                <h3 className="font-serif text-2xl text-[color:var(--tdt-ink)] mb-2">Special details or requests</h3>
                <div className="hr-gold mb-10" />
                <Field label="Anything else we should know">
                  <textarea data-testid="field-notes" value={form.notes} onChange={update('notes')} className="tdt-textarea" rows={5} placeholder="Surprises, finishing touches, guest names, dress code, accessibility…" />
                </Field>
              </div>

              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 pt-4 reveal">
                <p className="text-xs uppercase tracking-[0.22em] text-[color:var(--tdt-muted)]">All enquiries are treated in strict confidence.</p>
                <button data-testid="submit-enquiry-btn" type="submit" disabled={submitting} className="btn-pill btn-gold disabled:opacity-60 disabled:cursor-not-allowed">
                  {submitting ? 'Submitting…' : <>Submit Enquiry <ArrowRight size={14} /></>}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="tdt-label">{label}{required && <span className="text-[color:var(--tdt-gold)]"> •</span>}</span>
      {children}
    </label>
  );
}
